import React from "react";
import Footer from "./Footer";
import Navbar from "./Navbar";
import SEO from "./SEO";
import { FaFileSignature, FaUserCheck, FaBalanceScale, FaHandshake } from "react-icons/fa";

const TermsConditions: React.FC = () => {
  const termsSections = [
    {
      title: "1. Core Introduction",
      icon: <FaHandshake />,
      content:
        `${import.meta.env.VITE_COMPANY_NAME} provides institutional lending solutions under the direct regulatory oversight of ${import.meta.env.VITE_COMPANY_NBFC}. We are dedicated to providing clear, transparent, and ethical financial services.`,
    },
    {
      title: "2. Eligibility Standards",
      icon: <FaUserCheck />,
      list: [
        "Age: 21 to 60 years at the time of application.",
        "Employment: Stable salaried income from recognized Indian organizations.",
        `Income: Minimum monthly thresholds as governed by ${import.meta.env.VITE_COMPANY_NBFC} standards.`,
        "Financial Health: Satisfactory credit score and residency in India.",
      ],
    },
    {
      title: "3. Transparency & Fees",
      icon: <FaBalanceScale />,
      content:
        "Interest rates remain fixed throughout the loan tenure. Applicable fees—including processing and late payment charges—are clearly outlined in the primary loan agreement. No hidden charges, ever.",
    },
    {
      title: "4. Digital Compliance",
      icon: <FaFileSignature />,
      content:
        "Repayments are managed via NACH or modern digital methods (UPI). Your data is handled with enterprise-grade security and is used strictly for processing and communication purposes.",
    },
  ];

  return (
    <div className="min-h-screen bg-white text-text-primary px-0 mx-0 overflow-x-hidden font-sans selection:bg-secondary/10 selection:text-secondary">
      <SEO
        title="Terms & Conditions"
        description={`Clear and transparent lending agreements with ${import.meta.env.VITE_COMPANY_NAME}. Understand your rights and responsibilities.`}
        keywords={`loan terms, personal loan agreement, fintech transparency`}
      />
      
      <div className="fixed inset-0 pointer-events-none z-0 bg-gradient-to-b from-white via-gray-50/30 to-white" />
      
      <div className="relative z-50">
        <Navbar />
      </div>

      <main className="relative z-10 pt-44 pb-20 md:pb-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          
          <header className="mb-20">
             <div className="flex items-center gap-3 px-6 py-2 bg-white border border-secondary/20 shadow-xl w-fit rounded-full mb-10 transition-transform hover:scale-105">
                <div className="w-2 h-2 bg-secondary rounded-full animate-pulse" />
                <span className="text-[10px] font-black tracking-[0.3em] text-text-secondary uppercase">Institutional Guidelines</span>
              </div>
            <h1 className="text-5xl md:text-8xl font-black tracking-tighter mb-10 text-text-primary leading-[0.9]">
              Fair <span className="text-secondary">Play.</span>
            </h1>
            <p className="text-text-secondary text-lg md:text-2xl font-medium leading-relaxed max-w-2xl">
              Honesty is our policy. These terms ensure that both you and {import.meta.env.VITE_COMPANY_NAME} are protected and informed.
            </p>
          </header>

          <div className="space-y-12">
            {termsSections.map((section, idx) => (
              <div key={idx} className="bg-white p-12 md:p-16 rounded-[3rem] border border-gray-100 shadow-xl relative overflow-hidden transition-all hover:shadow-2xl">
                <div className="flex flex-col md:flex-row gap-10">
                  <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center text-primary text-3xl shrink-0">
                    {section.icon}
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl md:text-4xl font-black mb-8 text-text-primary tracking-tight">
                      {section.title}
                    </h2>
                    {section.content && (
                      <p className="text-text-secondary text-lg md:text-xl leading-relaxed font-medium mb-6">
                        {section.content}
                      </p>
                    )}
                    {section.list && (
                      <ul className="space-y-4 pt-4">
                        {section.list.map((li, i) => (
                          <li key={i} className="flex items-start gap-4 text-text-secondary font-medium text-lg">
                            <div className="w-2 h-2 bg-secondary mt-2.5 rounded-full shrink-0" />
                            <span>{li}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {/* Legal Protection Card */}
            <div className="bg-secondary text-white p-12 md:p-20 rounded-[3rem] shadow-2xl relative overflow-hidden">
                <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-white/10 rounded-full blur-[100px]" />
                <h3 className="text-2xl md:text-4xl font-black mb-10 tracking-tighter">Governance & Disputes</h3>
                <p className="text-white/90 text-lg md:text-xl leading-relaxed font-medium mb-12">
                  All digital lending activities are governed by the laws of India. Any disputes will be handled under the jurisdiction of Delhi courts, with a primary focus on amicable resolution.
                </p>
                <div className="flex flex-wrap gap-10 border-t border-white/20 pt-10">
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-white/60 mb-2">Institutional Partner</div>
                    <div className="font-black text-sm">{import.meta.env.VITE_COMPANY_NBFC}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-white/60 mb-2">Global Oversight</div>
                    <div className="font-black text-sm">Regulatory Compliance India</div>
                  </div>
                </div>
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsConditions;
